import { Component, EventEmitter, Input, NgZone, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from 'src/app/services/api.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-admin-reset-password',
  templateUrl: './admin-reset-password.component.html',
  styleUrls: ['./admin-reset-password.component.scss']
})
export class AdminResetPasswordComponent {

  constructor(private api: ApiService,
    private spinner: NgxSpinnerService,
    private notification: NotificationService,
    private cognitoService: CognitoService) { }

  @Output() resetModalClose = new EventEmitter<false>();
  @Output() modalClose = new EventEmitter<{ value: boolean }>();

  public error: boolean = false;

  public user: any = {};
  public message: string = '';
  public type: string = '';
  public show_old_password: boolean = false;
  public show_new_password: boolean = false;
  public show_confirm_password: boolean = false;
  public email: string = '';

  ngOnInit(): void {
    this.getUserEmail();
    this.error = false;
  }

  validForms() {
    this.error = false;

    if (!this.user.old_password) {
      this.showNotifications("Old Password Required", 'warning');
      return;
    }

    if (!this.user.new_password) {
      this.showNotifications("New Password Required", 'warning');
      return;
    }

    if (!this.user.confirm_password) {
      this.showNotifications("Confirm Password Required", 'warning');
      return;
    }

    if (this.user.old_password.length < 12 || this.user.new_password.length < 12 || this.user.confirm_password.length < 12) {
      this.showNotifications("Password length should be more than or equal to 12", 'warning');
      return;
    }

    if (this.user.old_password === this.user.new_password) {
      this.showNotifications("Can not use current Password as a new Password", 'warning');
      return;
    }

    const password_regex = /^(?=.*[A-Za-z])(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%&*])[A-Za-z\d@#$%&*]*$/;

    if (!password_regex.test(this.user.new_password) || !password_regex.test(this.user.old_password)) {
      this.showNotifications("Password must include at least one upper case letter ('A-Z'), one lower case letter ('a-z'), one numeric value ('0-9') and one special character value ('@,#,$,%,&,*')", 'warning');
      return;
    }

    if (this.user.new_password !== this.user.confirm_password) {
      this.showNotifications("Password do not match", 'warning');
      return;
    }

  }

  showOldPassword(iconElement: any) {
    const passwordInput = document.querySelector('input[name="old_password"]');

    if (passwordInput && iconElement) {
      if (passwordInput.getAttribute('type') === 'password') {
        passwordInput.setAttribute('type', 'text');
        iconElement.classList.remove('fa-eye-slash');
        iconElement.classList.add('fa-eye');
      } else {
        passwordInput.setAttribute('type', 'password');
        iconElement.classList.remove('fa-eye');
        iconElement.classList.add('fa-eye-slash');
      }
    }
  }

  showNewPassword(iconElement: any) {
    const passwordInput = document.querySelector('input[name="new_password"]');

    if (passwordInput && iconElement) {
      if (passwordInput.getAttribute('type') === 'password') {
        passwordInput.setAttribute('type', 'text');
        iconElement.classList.remove('fa-eye-slash');
        iconElement.classList.add('fa-eye');
      } else {
        passwordInput.setAttribute('type', 'password');
        iconElement.classList.remove('fa-eye');
        iconElement.classList.add('fa-eye-slash');
      }
    }
  }

  showConfirmPassword(iconElement: any) {
    const passwordInput = document.querySelector('input[name="confirm_password"]');

    if (passwordInput && iconElement) {
      if (passwordInput.getAttribute('type') === 'password') {
        passwordInput.setAttribute('type', 'text');
        iconElement.classList.remove('fa-eye-slash');
        iconElement.classList.add('fa-eye');
      } else {
        passwordInput.setAttribute('type', 'password');
        iconElement.classList.remove('fa-eye');
        iconElement.classList.add('fa-eye-slash');
      }
    }
  }

  private async getUserEmail() {
    const authenticatedUser = await this.cognitoService.getUserdata();
    if (authenticatedUser && authenticatedUser.email) {
      this.email = authenticatedUser.email;
    }
  }

  public async resetPasswordCliked() {
    try {
      this.spinner.show();
      this.validForms();

      if (this.error) {
        this.spinner.hide();
        return;
      }

      let data = {
        email: this.email,
        password: this.user.new_password
      }

      const observable = await this.api.checkPasswordDuplication(data);
      const result = await observable.toPromise();

      if (!result) {
        this.spinner.hide();
        this.showNotifications('User validation failed', 'error');
      }

      if (result.status === 200) {
        this.spinner.hide();
        this.updatePassword();
      } else {
        this.spinner.hide();
        this.showNotifications('Can not use last three used passwords, please create a new password', 'error');
      }
    } catch (error: any) {
      this.spinner.hide();
      this.showNotifications(error.message, 'error');
    }
  }

  updatePassword() {
    this.spinner.show();
    this.validForms();

    if (this.error) {
      this.spinner.hide();
      return;
    }

    if (this.user && this.user.old_password.trim().length !== 0 && this.user.new_password.trim().length !== 0) {
      const newPassword = this.user.new_password.trim();
      const oldPassword = this.user.old_password.trim();
      this.cognitoService.handleUpdatePassword({ oldPassword, newPassword })
        .then(() => {
          this.changePassword();
        })
        .catch((error: any) => {
          this.spinner.hide();
          this.showNotifications(error.message, 'error');
        })
    } else {
      this.spinner.hide();
      this.showNotifications("Please enter valid password", 'warning');
    }
  }

  public async changePassword() {
    try {
      const newPassword = this.user.new_password;

      const observable = await this.api.resetPassword({ email: this.email, password: newPassword })
      const result = await observable.toPromise();
      if (!result) {
        this.spinner.hide();
        this.showNotifications('User validation failed', 'error');
      }
      if (result.status === 200) {
        this.passwordChanged();
      } else {
        this.spinner.hide();
        this.showNotifications(result.message, 'error');
      }
    } catch (error: any) {
      this.spinner.hide();
      this.showNotifications(error.message, 'error');
    }
  }

  deny() {
    this.modalClose.emit({ value: false });
  }

  passwordChanged() {
    this.resetModalClose.emit(false);
  }

  showNotifications(message: string, type: string) {
    this.error = true;
    this.message = message;
    this.type = type;
    if (this.type == 'error') {
      this.notification.showError(this.message);
    } else if (this.type === 'warning') {
      this.notification.showWarning(this.message)
    } else if (this.type === 'success') {
      this.notification.showSuccess(this.message)
    } else {
      this.notification.showError("An error occurred. Please try again.");
    }

  }

  notificationClose() {
    this.error = false;
  }
}
